// Level of Detail (LOD) System with Impostor Sprites
// Inspired by Lineage II's handling of massive battlefields
// Automatically switches between mesh detail levels based on camera distance

import * as THREE from 'three';

export interface LODConfig {
  highDistance: number;    // Distance at which to use high-detail mesh
  mediumDistance: number;  // Distance at which to switch to medium detail
  lowDistance: number;     // Distance at which to switch to low detail
  spriteDistance: number;  // Distance at which to use billboard sprite
  cullDistance: number;    // Distance at which to hide completely
}

const DEFAULT_LOD_CONFIG: LODConfig = {
  highDistance: 20,
  mediumDistance: 50,
  lowDistance: 100,
  spriteDistance: 150,
  cullDistance: 250,
};

interface LODEntity {
  id: string;
  position: THREE.Vector3;
  meshes: {
    high?: THREE.Object3D;
    medium?: THREE.Object3D;
    low?: THREE.Object3D;
  };
  sprite?: THREE.Sprite;
  currentLOD: 'high' | 'medium' | 'low' | 'sprite' | 'culled';
  color: number;
  scale: number;
}

export class LODSystem {
  private scene: THREE.Scene;
  private camera: THREE.Camera;
  private entities: Map<string, LODEntity> = new Map();
  private config: LODConfig;

  // Sprite texture cache
  private spriteTextures: Map<number, THREE.SpriteMaterial> = new Map();

  // Update throttling
  private updateCounter = 0;
  private readonly UPDATE_INTERVAL = 3; // Update LOD every N frames

  // Reusable objects
  private readonly _tempVec = new THREE.Vector3();

  // Batch processing
  private readonly batchSize = 50;
  private currentBatchStart = 0;

  constructor(scene: THREE.Scene, camera: THREE.Camera, config?: Partial<LODConfig>) {
    this.scene = scene;
    this.camera = camera;
    this.config = { ...DEFAULT_LOD_CONFIG, ...config };
  }

  public addEntity(
    id: string,
    position: THREE.Vector3,
    meshes: {
      high?: THREE.Object3D;
      medium?: THREE.Object3D;
      low?: THREE.Object3D;
    },
    color: number = 0xffffff,
    scale: number = 1
  ): void {
    // Create impostor sprite
    const sprite = this.createSprite(color, scale);
    sprite.position.copy(position);
    sprite.visible = false;

    const entity: LODEntity = {
      id,
      position,
      meshes,
      sprite,
      currentLOD: 'high',
      color,
      scale,
    };

    // Initially hide medium and low meshes
    if (meshes.medium) meshes.medium.visible = false;
    if (meshes.low) meshes.low.visible = false;

    this.entities.set(id, entity);
    this.scene.add(sprite);
  }

  public removeEntity(id: string): void {
    const entity = this.entities.get(id);
    if (!entity) return;

    if (entity.sprite) {
      this.scene.remove(entity.sprite);
      entity.sprite.material.dispose();
    }

    this.entities.delete(id);
  }

  public updateEntityPosition(id: string, position: THREE.Vector3): void {
    const entity = this.entities.get(id);
    if (!entity) return;

    entity.position.copy(position);

    // Update all mesh positions
    if (entity.meshes.high) entity.meshes.high.position.copy(position);
    if (entity.meshes.medium) entity.meshes.medium.position.copy(position);
    if (entity.meshes.low) entity.meshes.low.position.copy(position);
    if (entity.sprite) entity.sprite.position.copy(position);
  }

  private createSprite(color: number, scale: number): THREE.Sprite {
    // Check cache first
    let material = this.spriteTextures.get(color);

    if (!material) {
      // Create a simple colored circle texture
      const canvas = document.createElement('canvas');
      canvas.width = 32;
      canvas.height = 32;
      const ctx = canvas.getContext('2d')!;

      // Draw colored circle
      const colorHex = '#' + color.toString(16).padStart(6, '0');
      ctx.fillStyle = colorHex;
      ctx.beginPath();
      ctx.arc(16, 16, 14, 0, Math.PI * 2);
      ctx.fill();

      // Add slight outline
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 2;
      ctx.stroke();

      const texture = new THREE.CanvasTexture(canvas);
      material = new THREE.SpriteMaterial({
        map: texture,
        transparent: true,
        depthWrite: false,
      });

      this.spriteTextures.set(color, material);
    }

    const sprite = new THREE.Sprite(material.clone());
    sprite.scale.set(scale * 2, scale * 2, 1);
    return sprite;
  }

  public update(): void {
    // Throttle updates
    this.updateCounter++;
    if (this.updateCounter < this.UPDATE_INTERVAL) return;
    this.updateCounter = 0;

    const cameraPos = this.camera.position;
    const entityArray = Array.from(this.entities.values());

    // Process in batches for large entity counts
    const endIndex = Math.min(this.currentBatchStart + this.batchSize, entityArray.length);

    for (let i = this.currentBatchStart; i < endIndex; i++) {
      const entity = entityArray[i];
      this.updateEntityLOD(entity, cameraPos);
    }

    this.currentBatchStart = endIndex >= entityArray.length ? 0 : endIndex;
  }

  private updateEntityLOD(entity: LODEntity, cameraPos: THREE.Vector3): void {
    const distance = this._tempVec.copy(entity.position).distanceTo(cameraPos);
    let newLOD: 'high' | 'medium' | 'low' | 'sprite' | 'culled';

    if (distance > this.config.cullDistance) {
      newLOD = 'culled';
    } else if (distance > this.config.spriteDistance) {
      newLOD = 'sprite';
    } else if (distance > this.config.lowDistance) {
      newLOD = 'low';
    } else if (distance > this.config.mediumDistance) {
      newLOD = 'medium';
    } else {
      newLOD = 'high';
    }

    if (newLOD === entity.currentLOD) return;

    // Hide previous LOD
    this.setLODVisibility(entity, entity.currentLOD, false);

    // Show new LOD
    this.setLODVisibility(entity, newLOD, true);

    entity.currentLOD = newLOD;
  }

  private setLODVisibility(
    entity: LODEntity,
    lod: 'high' | 'medium' | 'low' | 'sprite' | 'culled',
    visible: boolean
  ): void {
    switch (lod) {
      case 'high':
        if (entity.meshes.high) entity.meshes.high.visible = visible;
        break;
      case 'medium':
        if (entity.meshes.medium) {
          entity.meshes.medium.visible = visible;
        } else if (entity.meshes.high) {
          // Fallback to high if medium doesn't exist
          entity.meshes.high.visible = visible;
        }
        break;
      case 'low':
        if (entity.meshes.low) {
          entity.meshes.low.visible = visible;
        } else if (entity.meshes.medium) {
          entity.meshes.medium.visible = visible;
        } else if (entity.meshes.high) {
          entity.meshes.high.visible = visible;
        }
        break;
      case 'sprite':
        if (entity.sprite) entity.sprite.visible = visible;
        break;
      case 'culled':
        // Everything hidden - nothing to do
        break;
    }
  }

  public getLODStats(): { high: number; medium: number; low: number; sprite: number; culled: number } {
    const stats = { high: 0, medium: 0, low: 0, sprite: 0, culled: 0 };
    for (const entity of this.entities.values()) {
      stats[entity.currentLOD]++;
    }
    return stats;
  }

  public setConfig(config: Partial<LODConfig>): void {
    Object.assign(this.config, config);
  }

  public destroy(): void {
    for (const entity of this.entities.values()) {
      if (entity.sprite) {
        this.scene.remove(entity.sprite);
      }
    }
    this.entities.clear();

    for (const material of this.spriteTextures.values()) {
      material.map?.dispose();
      material.dispose();
    }
    this.spriteTextures.clear();
  }
}

// Mesh LOD Generator - creates simplified meshes from high-detail ones
export class MeshLODGenerator {
  // Generate medium detail mesh (reduce faces by ~50%)
  public static generateMediumLOD(geometry: THREE.BufferGeometry): THREE.BufferGeometry {
    // Simple decimation by skipping every other vertex
    const positions = geometry.attributes.position.array;
    const newPositions: number[] = [];

    for (let i = 0; i < positions.length; i += 6) {
      newPositions.push(positions[i], positions[i + 1], positions[i + 2]);
    }

    const newGeometry = new THREE.BufferGeometry();
    newGeometry.setAttribute('position', new THREE.Float32BufferAttribute(newPositions, 3));
    newGeometry.computeVertexNormals();

    return newGeometry;
  }

  // Generate low detail mesh (reduce faces by ~75%)
  public static generateLowLOD(geometry: THREE.BufferGeometry): THREE.BufferGeometry {
    const positions = geometry.attributes.position.array;
    const newPositions: number[] = [];

    for (let i = 0; i < positions.length; i += 12) {
      newPositions.push(positions[i], positions[i + 1], positions[i + 2]);
    }

    const newGeometry = new THREE.BufferGeometry();
    newGeometry.setAttribute('position', new THREE.Float32BufferAttribute(newPositions, 3));
    newGeometry.computeVertexNormals();

    return newGeometry;
  }

  // Create a simple box approximation for very low LOD
  public static generateBoxLOD(geometry: THREE.BufferGeometry): THREE.BufferGeometry {
    geometry.computeBoundingBox();
    const box = geometry.boundingBox!;
    const size = new THREE.Vector3();
    box.getSize(size);

    return new THREE.BoxGeometry(size.x, size.y, size.z);
  }
}
